/** What the engine's model cache does in this tab. */
export type ModelCacheMode = 'on' | 'off' | 'validate'

/** Where a support session's choice is kept for the rest of the tab's session. */
const OVERRIDE_KEY = 'motif:model-cache'

type OverrideStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** The support request in the URL, if it has a recognized one. */
const requestInUrl = (search: string): ModelCacheMode | undefined => {
  const params = new URLSearchParams(search)
  const requested = params.has('validate-cache') ? 'validate' : params.get('model-cache')
  return requested === 'on' || requested === 'off' || requested === 'validate'
    ? requested
    : undefined
}

/**
 * Keeps a support session's request from the URL for the rest of the tab's session. The app
 * calls this at startup, before its router can redirect the URL away (`/` → `/organization`),
 * and [`modelCacheMode`] calls it again for later navigations:
 *
 * - `?validate-cache`: load from the server as usual, compare what the cache would have served,
 *   and log the verdict to the console (`model cache validation of …`);
 * - `?model-cache=off`: bypass the cache, deleting this browser's stored copies;
 * - `?model-cache=on`: back to normal.
 */
export const rememberModelCacheRequest = (search: string, storage?: OverrideStorage): void => {
  const requested = requestInUrl(search)
  try {
    if (requested === 'on') {
      storage?.removeItem(OVERRIDE_KEY)
    } else if (requested) {
      storage?.setItem(OVERRIDE_KEY, requested)
    }
  } catch {
    // Storage is blocked: only a request still in the URL counts.
  }
}

/**
 * The model cache mode for this tab. The environment sets `on` or `off` (`VITE_MODEL_CACHE`);
 * `off` is the kill switch and always wins. Otherwise a support request in the URL decides, or
 * without one, a request remembered earlier in this tab's session
 * ([`rememberModelCacheRequest`]).
 */
export const modelCacheMode = (
  environment: string | undefined,
  search: string,
  storage?: OverrideStorage
): ModelCacheMode => {
  if (environment?.trim() !== 'on') {
    return 'off'
  }
  rememberModelCacheRequest(search, storage)
  // The URL wins even when remembering it failed and storage still holds an older request.
  const requested = requestInUrl(search)
  if (requested) {
    return requested
  }
  let remembered: string | null | undefined
  try {
    remembered = storage?.getItem(OVERRIDE_KEY)
  } catch {
    // Storage is blocked: nothing was remembered.
  }
  return remembered === 'validate' || remembered === 'off' ? remembered : 'on'
}

/** This tab's `sessionStorage`, or `undefined` where the browser blocks it. */
export const tabSessionStorage = (): OverrideStorage | undefined => {
  try {
    return window.sessionStorage
  } catch {
    return undefined
  }
}
